import 'braid-design-system/reset';

import { HirerAuthProvider } from '@seek/hirer-auth-react';
import { HirerApolloProvider } from '@seek/hirer-graphql-react';
import { HubbleProvider } from '@seek/hubble';
import type { Locale } from '@seek/melways-sites';
import { BraidProvider, ToastProvider } from 'braid-design-system';
import apacTheme from 'braid-design-system/themes/apac';
import { StrictMode } from 'react';
import { Provider as ReduxProvider } from 'react-redux';
import { Outlet } from 'react-router';

import { hubble } from 'src/app/analytics/hubble';
import { CustomVocabProvider } from 'src/app/contexts/CustomVocabProvider';
import { ExpressUIConfigProvider } from 'src/app/contexts/ExpressUIConfigProvider';
import type { ConfigOptions } from 'src/app/contexts/ExpressUIConfigProvider/types';
import { FeatureToggleProvider } from 'src/app/featureToggle';
import { store } from 'src/app/state/store';
import { apolloConfig } from 'src/app/utils/apolloClient';
import { isDev, isEnvMocked } from 'src/app/utils/environment';
import RootContainer from 'src/app/views/containers/RootContainer';
import { MainLayout } from 'src/app/views/layouts/MainLayout';
import type { EmployerSiteName } from 'src/types/app';

export interface AppProps {
  config: ConfigOptions;
  language: Locale;
  site: EmployerSiteName;
}

export const App = ({ config, language, site }: AppProps) => (
  <StrictMode>
    <BraidProvider theme={apacTheme}>
      <HubbleProvider hubble={hubble}>
        <ToastProvider>
          <HirerAuthProvider useMockedAuth={isDev() && isEnvMocked()}>
            <HirerApolloProvider {...apolloConfig}>
              <ReduxProvider store={store}>
                <ExpressUIConfigProvider config={config}>
                  <FeatureToggleProvider>
                    <CustomVocabProvider language={language}>
                      <MainLayout site={site} language={language}>
                        <RootContainer site={site}>
                          <Outlet />
                        </RootContainer>
                      </MainLayout>
                    </CustomVocabProvider>
                  </FeatureToggleProvider>
                </ExpressUIConfigProvider>
              </ReduxProvider>
            </HirerApolloProvider>
          </HirerAuthProvider>
        </ToastProvider>
      </HubbleProvider>
    </BraidProvider>
  </StrictMode>
);
export default App;
